import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TickerPriceChart } from '../components/TickerPriceChart'
import type { BarInterval } from '../api/marketData'
import { ChartErrorBoundary } from '../components/chart/ChartErrorBoundary'
import { useAuthStore } from '../auth/useAuthStore'

// Intervals the chart's toolbar offers — also the set we accept from the URL.
const VALID_INTERVALS: BarInterval[] = ['1m', '5m', '15m', '1h', '1d']

function parseInterval(raw: string | null): BarInterval {
  return raw && (VALID_INTERVALS as string[]).includes(raw) ? (raw as BarInterval) : '5m'
}

/**
 * Standalone single-chart window opened via the "detach" button on any chart.
 * Rendered ABOVE <ConnectedShell> (see App.tsx) so this window doesn't double-
 * run the portfolio-snapshot recorder that the main window already owns.
 * Symbol + interval are seeded from the URL but then held in
 * local state so the window's own type-to-search and interval picker work.
 * Indicator config comes from the shared (DB-synced) settings store, so the
 * popout shows the same studies as the chart it was detached from.
 */
export function ChartPopoutPage() {
  const [params] = useSearchParams()
  const schwabAttached = useAuthStore((s) =>
    s.platforms.some((p) => p.platformId === 'schwab'),
  )

  const [symbol, setSymbol] = useState(() =>
    (params.get('symbol') ?? 'SPY').toUpperCase(),
  )
  const [interval, setInterval] = useState<BarInterval>(() =>
    parseInterval(params.get('interval')),
  )

  useEffect(() => {
    document.title = symbol ? `${symbol} · Chart` : 'Chart'
  }, [symbol])

  if (!schwabAttached) {
    return (
      <div className="flex h-dvh items-center justify-center bg-zinc-950 px-4 text-sm text-zinc-500">
        Connect a Schwab account to see price charts.
      </div>
    )
  }

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-zinc-950">
      <button
        type="button"
        onClick={() => { window.close(); window.history.back() }}
        aria-label="Close chart"
        className="lg:hidden fixed top-3 left-3 z-20 flex items-center gap-1.5 rounded bg-zinc-800/90 px-3 py-1.5 text-xs font-medium text-zinc-300 backdrop-blur-sm hover:text-zinc-100"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>
      <ChartErrorBoundary resetKey={symbol}>
        <TickerPriceChart
          symbol={symbol}
          gridCell
          active
          popout
          defaultInterval={interval}
          onIntervalChange={setInterval}
          onSymbolChange={(s) => setSymbol(s.toUpperCase())}
        />
      </ChartErrorBoundary>
    </div>
  )
}
